import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { selectCoinDepositList } from '@redux/selectors/userSelectors';
import { useAppSelector } from '@redux/store';

import { useVisitDailyBonus } from '@/common/hooks/visit-daily-bonus';
import { type BuyCoinItemProps } from '@/common/types/components';
import { ModalContext } from '@/components/Modals/Modal/config';
import {
  ShopBadge,
  ShopBuyButton,
  ShopTitle,
} from '@/components/Shop/ui/ShopUi';
import { Picture } from '@/components/ui/Picture/Picture';

import { DailyTgemCard } from './DailyTgemCard/DailyTgemCard';
import { TgemCard } from './TgemCard/TgemCard';
import {
  packArtFor,
  packNames,
  tgemPackBonus,
  tgemPackVipDays,
} from './config';

import css from './styles.module.scss';

// Half prices (first purchase) can have a third decimal: 0.745 → 0.75
const toUsd = (value?: number): number | undefined =>
  value === undefined ? undefined : Math.round(value * 100 + 1e-6) / 100;

/** "T-Gem" tab of the shop: daily bonus and the T-Gem packs. */
const CoinMarket: FC = () => {
  const { t } = useTranslation();
  const { withDiscount, list } = useAppSelector(selectCoinDepositList);
  const { openModal } = useContext(ModalContext);
  useVisitDailyBonus();

  const packs = [...list].sort((a, b) => a.coin - b.coin);
  const lastIndex = packs.length - 1;

  const buyPack = (activeOffer: BuyCoinItemProps): void => {
    openModal({
      renderKind: 'buySingleTgems',
      props: { activeOffer, source: 'shop' },
    });
  };

  return (
    <div className="w-full flex flex-col items-center gap-4 px-4 pt-4 pb-6">
      <ShopTitle>{t('Shine with T-Gems!')}</ShopTitle>
      {withDiscount && (
        <div className={css.firstBuy}>
          <span className="font-bold">{t('First-purchase offer')}:</span>
          <span className="font-black">{t('-50% Discount')}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 w-full">
        <DailyTgemCard />
        {packs.map((pack, index) => {
          const { base, bonus } = tgemPackBonus[pack.coin] ?? {
            base: pack.coin,
            bonus: 0,
          };
          const packArt = packArtFor(base);
          // Same places as in the design: 4th paid pack and the biggest one
          const isPopular = index === 3 && index !== lastIndex;
          const isBestDeal = index === lastIndex && lastIndex > 0;

          return (
            <TgemCard
              key={pack.coin}
              amount={base.toLocaleString('ru-RU')}
              caption={packNames[pack.coin]}
              bonus={bonus}
              vipDays={tgemPackVipDays[pack.coin]}
              art={
                <Picture
                  img={packArt.png}
                  webp={packArt.webp}
                  alt={`${pack.coin} t-gem`}
                  className="absolute block max-w-none"
                  style={packArt.style}
                />
              }
              badge={
                (isPopular || isBestDeal) && (
                  <ShopBadge
                    text={t(isBestDeal ? 'Best deal' : 'Popular')}
                    color="red"
                    size="sm"
                    raised
                  />
                )
              }
            >
              <ShopBuyButton
                usd={toUsd(pack.price)}
                oldUsd={withDiscount ? toUsd(pack.priceDiscount) : undefined}
                discount={withDiscount ? 50 : undefined}
                onClick={() => buyPack(pack)}
              />
            </TgemCard>
          );
        })}
      </div>
    </div>
  );
};

export { CoinMarket };
